import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Plus, ExternalLink } from 'lucide-react'
import { useCrawls, type Crawl } from '../hooks/useCrawls'

function statusBadge(status: Crawl['status']) {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    pending: 'secondary',
    running: 'default',
    completed: 'outline',
    failed: 'destructive',
    cancelled: 'secondary',
  }
  return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function CrawlListPage() {
  const { data, isLoading } = useCrawls()
  const crawls = data?.crawls || []
  const totalCount = data?.totalCount ?? crawls.length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Site Crawls</h1>
          <p className="text-muted-foreground">
            SEO audit crawls for your websites
            {totalCount > crawls.length && (
              <span className="ml-1">— showing {crawls.length} of {totalCount}</span>
            )}
          </p>
        </div>
        <Link to="/dashboard/crawls/new">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            New Crawl
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : crawls.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">No crawls yet. Start your first SEO audit.</p>
            <Link to="/dashboard/crawls/new">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Start a Crawl
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {crawls.map((crawl) => (
            <Link key={crawl.id} to={`/dashboard/crawls/${crawl.id}`} className="block">
              <Card className="hover:bg-accent/50 transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-medium flex items-center gap-2">
                      {crawl.domain}
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </CardTitle>
                    {statusBadge(crawl.status)}
                  </div>
                  <CardDescription className="text-xs truncate">{crawl.url}</CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex gap-6 text-sm text-muted-foreground">
                    <span>{crawl.pages_crawled} / {crawl.pages_found} pages</span>
                    {crawl.issues_count > 0 && (
                      <span>{crawl.issues_count} issue{crawl.issues_count !== 1 ? 's' : ''}</span>
                    )}
                    <span>{formatDate(crawl.created_at)}</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
